import { Grid, Theme } from '@material-ui/core';
import { createStyles, makeStyles } from '@material-ui/core/styles';
import { isValid, parse } from 'date-fns';
import { FC, useCallback } from 'react';
import DateFieldString from 'ui-kit/inputs/DateField/DateFieldString';
import { AgentNetTextInput } from 'ui-kit/inputs/TextField';
import { PersonBase } from '../../../types';

interface PersonalInfoFormProps {
  data: PersonBase;
  prefix?: string;
  index?: number;
  secondaryIndex?: number;
  getFieldError: (fieldName: string) => { field: string; message: string } | undefined;
  onUpdate: (updates: Partial<PersonBase>) => void;
  onValidationError?: (fieldName: string, message: string) => void;
  onValidationClear?: (fieldName: string) => void;
  clearFieldError?: (fieldName: string) => void;
  showValidation?: boolean;
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    container: {
      padding: theme.spacing(0, 2, 1.5, 0),
      '&:nth-child(4n)': {
        paddingRight: 0,
      },
    },
  }),
);

const PersonalInfoForm: FC<PersonalInfoFormProps> = ({
  data,
  prefix = '',
  index,
  secondaryIndex,
  getFieldError,
  onUpdate,
  onValidationError,
  onValidationClear,
  clearFieldError,
  showValidation = false,
}) => {
  // Generate index suffix for data-testid based on provided indices
  const getIndexSuffix = () => {
    if (index !== undefined && secondaryIndex !== undefined) {
      return `${index}-${secondaryIndex}`;
    } else if (index !== undefined) {
      return `${index}`;
    }
    return '';
  };
  const indexSuffix = getIndexSuffix();
  const classes = useStyles();
  const handleFieldUpdate = useCallback(
    (fieldName: string, value: string) => {
      const updates = { [fieldName]: value };
      onUpdate(updates);
    },
    [onUpdate],
  );

  const handleDateOfBirthChange = (date: string | null) => {
    handleFieldUpdate('dateOfBirth', date || '');

    // Validate the date format
    if (date && date.trim() !== '') {
      // Check if the date string is complete (not partially filled like 02/__/____)
      const isPartialDate = date.includes('_');

      if (isPartialDate) {
        // If date is incomplete, add validation error
        if (onValidationError) {
          onValidationError('dateOfBirth', 'Invalid date');
        }
      } else {
        // Parse and validate the complete date
        const parsedDate = parse(date, 'MM/dd/yyyy', new Date());
        if (!isValid(parsedDate)) {
          // Add validation error for invalid date
          if (onValidationError) {
            onValidationError('dateOfBirth', 'Invalid date');
          }
        } else {
          // Check if the date components are within valid ranges
          const parts = date.split('/');
          if (parts.length === 3) {
            const month = parseInt(parts[0], 10);
            const day = parseInt(parts[1], 10);
            const year = parseInt(parts[2], 10);

            // Validate month (1-12), day (1-31), and year (reasonable range)
            if (month < 1 || month > 12 || day < 1 || day > 31 || year < 1900 || year > 2100) {
              if (onValidationError) {
                onValidationError('dateOfBirth', 'Invalid date');
              }
            } else {
              // Clear validation error if date is valid
              if (onValidationClear) {
                onValidationClear('dateOfBirth');
              }
            }
          } else {
            // Clear validation error if date is valid
            if (onValidationClear) {
              onValidationClear('dateOfBirth');
            }
          }
        }
      }
    } else {
      // Clear validation error if date is empty
      if (onValidationClear) {
        onValidationClear('dateOfBirth');
      }
    }
  };

  return (
    <Grid container>
      <Grid item xs={6} md={3} className={classes.container}>
        <AgentNetTextInput
          label="First Name"
          name="firstName"
          value={data.firstName || ''}
          onChange={(e) => {
            handleFieldUpdate('firstName', e.target.value);
            if (e.target.value && clearFieldError) {
              clearFieldError('firstName');
            }
          }}
          required
          error={!!getFieldError('firstName')}
          helperText={getFieldError('firstName')?.message}
          data-testid={`fincen-${prefix.toLowerCase()}${indexSuffix ? `-${indexSuffix}` : ''}-firstname-input`}
          fullWidth
        />
      </Grid>
      <Grid item xs={6} md={3} className={classes.container}>
        <AgentNetTextInput
          label="Middle Name"
          name="middleName"
          value={data.middleName || ''}
          onChange={(e) => handleFieldUpdate('middleName', e.target.value)}
          data-testid={`fincen-${prefix.toLowerCase()}${indexSuffix ? `-${indexSuffix}` : ''}-middlename-input`}
          fullWidth
        />
      </Grid>
      <Grid item xs={6} md={3} className={classes.container}>
        <AgentNetTextInput
          label="Last Name"
          name="lastName"
          value={data.lastName || ''}
          onChange={(e) => {
            handleFieldUpdate('lastName', e.target.value);
            if (e.target.value && clearFieldError) {
              clearFieldError('lastName');
            }
          }}
          required
          error={!!getFieldError('lastName')}
          helperText={getFieldError('lastName')?.message}
          data-testid={`fincen-${prefix.toLowerCase()}${indexSuffix ? `-${indexSuffix}` : ''}-lastname-input`}
          fullWidth
        />
      </Grid>
      <Grid item xs={6} md={3} className={classes.container}>
        <AgentNetTextInput
          label="Suffix"
          name="suffix"
          value={data.suffix || ''}
          onChange={(e) => handleFieldUpdate('suffix', e.target.value)}
          data-testid={`fincen-${prefix.toLowerCase()}${indexSuffix ? `-${indexSuffix}` : ''}-suffix-input`}
          fullWidth
        />
      </Grid>
      <Grid item xs={6} md={3} className={classes.container}>
        <DateFieldString
          label="Date of Birth"
          value={data.dateOfBirth || ''}
          onChange={handleDateOfBirthChange}
          error={!!getFieldError('dateOfBirth')}
          helperText={getFieldError('dateOfBirth')?.message}
          qaAttribute={`fincen-${prefix.toLowerCase()}${indexSuffix ? `-${indexSuffix}` : ''}-date-of-birth-input`}
          fullWidth
          placeholder="mm/dd/yyyy"
          format="MM/dd/yyyy"
          shouldPrioritizeOnChange={false}
          showValidation={showValidation}
          required
        />
      </Grid>
    </Grid>
  );
};

export default PersonalInfoForm;
